import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormGroup, FormLabel, Stack, TextField } from "@mui/material";
import { useCallback, useEffect, useReducer, useState } from "react";
import api, { RetailerDto, RetailerVendorDto, RetailerVendorSettingsSchedulerMode, RetailerVendorSettingsSendMethod, RetailerVendorUpdateDto } from "../../../api";
import { TokensControl } from "../../controls";
import { AmazonSettingsControl, CommerceHubSettingsControl, FaireSettingsControl, FtpSettingsControl, OverstockSettingsControl, SftpSettingsControl, SmtpSettingsControl, WalmartSettingsControl } from "../../settings";
import { createStateReducer, notif } from "../../utils/util";
import { RetailerVendorMailSettingsControl } from "./RetailerVendorMailSettingsControl";
import { SchedulerModeList } from "./SchedulerModeList";
import { SendMethodList } from "./SendMethodList";
import { VendorList } from "./VendorList";

export interface RetailerVendorDialogAction {
  open: boolean;
  retailer?: RetailerDto;
  vendorId?: string;
}

export interface RetailerVendorDialogProps {
  action: RetailerVendorDialogAction;
  onSubmit?: (dto: RetailerVendorDto | null, action: "closed" | "added" | "updated") => void;
}

type StateAction = "none" | "add" | "edit";

const getRetailerVendor = async (retailerId?: string, vendorId?: string): Promise<RetailerVendorDto | undefined> => {
  if (retailerId && vendorId) {
    try {
      return await api.retailerVendor.get(retailerId, vendorId);
    } catch (err) {
      // DO NOTHING
    }
  }
  return undefined;
};

const toRetailerVendorUpdateDto = (dto: RetailerVendorDto): RetailerVendorUpdateDto =>
  ({
    retailerId: dto.retailerId,
    vendorId: dto.vendorId,
    settings: dto.settings,
    isEnabled: dto.isEnabled,
  } as any);

export const RetailerVendorDialog = (props: RetailerVendorDialogProps) => {
  const { action, onSubmit } = props;

  const reducer = createStateReducer<RetailerVendorUpdateDto>();
  const [state, dispatch] = useReducer(reducer, null);
  const [vendorId, setVendorId] = useState("");
  const [stateAction, setStateAction] = useState<StateAction>("none");

  const loadState = useCallback(async () => {
    if (action.open && action.retailer?.id) {
      // if current state is "none" then dialog is opening
      const opening = stateAction === "none";
      let dto: RetailerVendorDto | undefined;
      if (opening) {
        // opening, load vendor from props
        dto = await getRetailerVendor(action.retailer.id, action.vendorId);
      } else {
        // already opened, load from selected vendor in state
        dto = await getRetailerVendor(action.retailer.id, vendorId);
      }
      if (dto) {
        dispatch({ state: toRetailerVendorUpdateDto(dto), replace: true });
        setStateAction("edit");
      } else {
        if (opening) {
          dispatch({ state: { retailerId: action.retailer.id, vendorId: "", settings: { sendMethod: RetailerVendorSettingsSendMethod.None } as any, isEnabled: true }, replace: true });
        }
        setStateAction("add");
      }
    } else {
      setStateAction("none");
    }
  }, [action, vendorId]);

  const handleAccept = async () => {
    try {
      const dto = await api.retailerVendor.update(state as any);
      if (onSubmit) onSubmit(dto, stateAction === "add" ? "added" : "updated");
      setStateAction("none");
    } catch (err) {
      notif(err, "error");
    }
  };
  const handleClose = () => {
    if (onSubmit) onSubmit(null, "closed");
    setStateAction("none");
  };

  const onVendorChanged = async (vendorId: string) => {
    dispatch({ state: { vendorId } });
    setVendorId(vendorId);
  };

  useEffect(() => {
    if (action.open && vendorId) {
      loadState();
    }
  }, [vendorId]);

  useEffect(() => {}, [state]);

  useEffect(() => {
    if (action.open && action.retailer?.id) {
      setVendorId(action.vendorId ?? "");
      loadState();
    } else {
      setStateAction("none");
      setVendorId("");
    }
  }, [action]);

  return (
    <Dialog open={stateAction !== "none"} onClose={handleClose} maxWidth="xl">
      <DialogTitle>
        {stateAction.toUpperCase()} RETAILER '{action?.retailer?.name}' VENDOR
      </DialogTitle>
      <DialogContent dividers>
        <FormGroup>
          <Stack spacing={1} direction="row">
            <VendorList vendorId={state?.vendorId ?? ""} onChange={onVendorChanged} />
            <SendMethodList value={state?.settings?.sendMethod ?? RetailerVendorSettingsSendMethod.None} onChange={(value) => dispatch({ state: { settings: { sendMethod: value } as any } })} />
            <FormControlLabel label="Enabled" control={<Checkbox checked={state?.isEnabled ?? true} onChange={(e, c) => dispatch({ state: { isEnabled: c } })} />} />
          </Stack>
          <Divider sx={{ paddingTop: 10 }} />
          <FormControl component="fieldset" sx={{ paddingTop: 10 }}>
            <FormGroup>
              <Stack spacing={2} direction="row" divider={<Divider orientation="vertical" flexItem />}>
                {state?.settings?.sendMethod === RetailerVendorSettingsSendMethod.Smtp && (
                  <SmtpSettingsControl settings={state?.settings?.smtp ?? ({} as any)} onChange={(value) => dispatch({ state: { settings: { smtp: value as any } as any } })} />
                )}
                {(state?.settings?.sendMethod === RetailerVendorSettingsSendMethod.Smtp ||
                  state?.settings?.sendMethod === RetailerVendorSettingsSendMethod.SmtpRetailer ||
                  state?.settings?.sendMethod === RetailerVendorSettingsSendMethod.SmtpSystem) && (
                  <RetailerVendorMailSettingsControl settings={state?.settings?.mail ?? ({} as any)} onChange={(value) => dispatch({ state: { settings: { mail: value as any } as any } })} />
                )}
                {state?.settings?.sendMethod === RetailerVendorSettingsSendMethod.Sftp && (
                  <SftpSettingsControl settings={state?.settings?.sftp ?? ({} as any)} onChange={(value) => dispatch({ state: { settings: { sftp: value as any } as any } })} />
                )}
                {state?.settings?.sendMethod === RetailerVendorSettingsSendMethod.Ftp && (
                  <FtpSettingsControl settings={state?.settings?.ftp ?? ({} as any)} onChange={(value) => dispatch({ state: { settings: { ftp: value as any } as any } })} />
                )}
                {state?.settings?.sendMethod === RetailerVendorSettingsSendMethod.Walmart && (
                  <WalmartSettingsControl settings={state?.settings?.walmart ?? ({} as any)} onChange={(value) => dispatch({ state: { settings: { walmart: value as any } as any } })} />
                )}
                {state?.settings?.sendMethod === RetailerVendorSettingsSendMethod.CommerceHub && (
                  <CommerceHubSettingsControl settings={state?.settings?.commerceHub ?? ({} as any)} onChange={(value) => dispatch({ state: { settings: { commerceHub: value as any } as any } })} />
                )}
                {state?.settings?.sendMethod === RetailerVendorSettingsSendMethod.Amazon && (
                  <AmazonSettingsControl settings={state?.settings?.amazon ?? ({} as any)} onChange={(value) => { dispatch({ state: { settings: { amazon: value as any } as any } }); }} />
                )}
                {state?.settings?.sendMethod === RetailerVendorSettingsSendMethod.Overstock && (
                  <OverstockSettingsControl settings={state?.settings?.overstock ?? ({} as any)} onChange={(value) => { dispatch({ state: { settings: { overstock: value as any } as any } }); }} />
                )}
                {state?.settings?.sendMethod === RetailerVendorSettingsSendMethod.Faire && (
                  <FaireSettingsControl settings={state?.settings?.faire ?? ({} as any)} onChange={(value) => { dispatch({ state: { settings: { faire: value as any } as any } }); }} />
                )}
                <FormControl component="fieldset" sx={{ minWidth: 250 }}>
                  <FormLabel component="legend">Other settings</FormLabel>
                  <FormGroup>
                    <TextField label="File name" variant="standard" value={state?.settings?.fileName ?? ""} onChange={(e) => dispatch({ state: { settings: { fileName: e.target.value } as any } })} />
                    <Box sx={{ p: 1 }} />
                    <SchedulerModeList
                      value={state?.settings?.schedulerMode ?? RetailerVendorSettingsSchedulerMode.System}
                      onChange={(value) => dispatch({ state: { settings: { schedulerMode: value } as any } })}
                    />
                    {state?.settings?.schedulerMode === RetailerVendorSettingsSchedulerMode.Custom && (
                      <TextField
                        label="Scheduler times"
                        variant="standard"
                        value={state?.settings?.schedulerTimes ?? ""}
                        onChange={(e) => dispatch({ state: { settings: { schedulerTimes: e.target.value } as any } })}
                      />
                    )}
                    {(state?.settings?.sendMethod === RetailerVendorSettingsSendMethod.Ftp ||
                      state?.settings?.sendMethod === RetailerVendorSettingsSendMethod.FtpRetailer ||
                      state?.settings?.sendMethod === RetailerVendorSettingsSendMethod.Sftp ||
                      state?.settings?.sendMethod === RetailerVendorSettingsSendMethod.SftpRetailer) && (
                      <TextField
                        label="Remote path"
                        variant="standard"
                        value={state?.settings?.remotePath ?? ""}
                        onChange={(e) => dispatch({ state: { settings: { remotePath: e.target.value } as any } })}
                      />
                    )}
                  </FormGroup>
                </FormControl>
                <TokensControl tokens={state?.settings?.tokens ?? []} onChange={(tokens) => dispatch({ state: { settings: { tokens } as any } })} />
              </Stack>
              <Divider sx={{ paddingTop: 10 }} />
              <Box sx={{ p: 1 }} />
            </FormGroup>
          </FormControl>
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleAccept}>Accept</Button>
        <Button onClick={handleClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
